import React, { useState, useMemo } from 'react';

import { Button, Typography, Card, notification, Tag, Popover, Spin } from 'antd';

const { Title } = Typography;

import { CloseOutlined, LoadingOutlined } from '@ant-design/icons';

import FileStore from 'stores/FileStore';

import { ErrorRender } from '@partsbadger/library';

import FileDownload from 'js-file-download';
import { observer } from 'mobx-react';

interface IProps {
    removeUpload: () => void;
}

const DisplayUploadFile: React.FC<IProps> = ({ removeUpload }) => {
    const [status, setStatus] = useState<string>('Waiting');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const fileName = FileStore.fileUploaded?.name?.split('.').shift() ?? '';

    const handleConvert2DFile = async () => {
        setStatus('Converting');
        await FileStore.convert2DFile()
            .then(() => setStatus('Done'))
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setStatus('Error');
            });
    };

    const handleConvert3DFile = async () => {
        setStatus('Converting');

        await FileStore.convert3DFile()
            .then(() => setStatus('Done'))
            .catch(err => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                });
                setStatus('Error');
            });
    };

    const switchStatus = () => {
        switch (status) {
            case 'Converting':
                return <Tag color="orange">Converting</Tag>;
            case 'Done':
                return <Tag color="green">Converted</Tag>;
            case 'Error':
                return <Tag color="red">Error</Tag>;
            default:
                return <Tag color="volcano">Waiting</Tag>;
        }
    };

    const getStatus = useMemo(() => switchStatus(), [status]);

    const handleDelete = () => {
        setConfirmDelete(false);
        removeUpload();
        FileStore.cleanStore();
    };

    const handleDownload = async (type: string) => {
        const nameWithExtension = type === '3D' ? `${fileName}.step` : `${fileName}.pdf`;
        const blob = FileStore.fileForDownload;

        if (!blob) return false;

        await FileDownload(blob, nameWithExtension);

        return true;
    };

    return (
        <div>
            <>
                <Card bordered={true} bodyStyle={{ padding: '1rem' }} className="shadow-sm">
                    <Spin
                        spinning={status === 'Converting' ? true : false}
                        tip={'Converting...'}
                        indicator={<LoadingOutlined />}
                    >
                        <div className="flex flex-row justify-between items-center">
                            <Title level={4}>{fileName.toUpperCase()}</Title>
                            <span className={'link'}>
                                <Popover
                                    content={
                                        <div className="flex justify-between">
                                            <a onClick={() => setConfirmDelete(false)}>Close</a>
                                            <a
                                                className="text-red-600"
                                                onClick={() => {
                                                    handleDelete();
                                                }}
                                            >
                                                Delete
                                            </a>
                                        </div>
                                    }
                                    title="Delete Upload?"
                                    trigger="click"
                                    open={confirmDelete}
                                    onOpenChange={setConfirmDelete}
                                >
                                    <Button
                                        type="link"
                                        icon={<CloseOutlined />}
                                        // onClick={() => setConfirmDeleteModalVisible(true)}
                                    />
                                </Popover>
                            </span>
                        </div>
                        <div className="flex justify-between items-center mt-5 mb-2">
                            {status === 'Waiting' && <Tag color="green">Ready for Conversion</Tag>}

                            <span>Output Format: {FileStore?.fileType === '3D' ? 'Step' : 'PDF'}</span>
                            <span>Status: {getStatus}</span>

                            {status === 'Done' && (
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        handleDownload(FileStore.fileType);
                                    }}
                                >
                                    Download Converted File
                                </Button>
                            )}
                        </div>
                    </Spin>
                </Card>

                {status !== 'Done' && (
                    <div className="flex w-full justify-end">
                        <Button
                            type="primary"
                            onClick={() => {
                                FileStore.fileType === '3D' ? handleConvert3DFile() : handleConvert2DFile();
                            }}
                            loading={status === 'Converting' ? true : false}
                        >
                            Convert Selected File
                        </Button>
                    </div>
                )}
            </>
        </div>
    );
};

export default observer(DisplayUploadFile);
